"use client";

import { useEffect, useState } from "react";

const MINUTE = 60_000;

/**
 * How long ago, in the largest unit that still reads as a number.
 *
 * `Intl` rather than a hand-rolled table so the plural is the browser's problem
 * rather than ours.
 */
function relative(stamp: Date, now: number): string {
  const minutes = Math.round((stamp.getTime() - now) / MINUTE);
  if (minutes > -1) {
    return "just now";
  }

  const format = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (minutes > -60) {
    return format.format(minutes, "minute");
  }
  const hours = Math.round(minutes / 60);
  if (hours > -24) {
    return format.format(hours, "hour");
  }
  return format.format(Math.round(hours / 24), "day");
}

/** The absolute instant, which is what renders before any script has run. */
function absolute(stamp: Date): string {
  return stamp.toISOString().slice(0, 16).replace("T", " ") + " UTC";
}

/**
 * An instant, rendered as how long ago it was.
 *
 * Absolute until mounted, relative after. Both surfaces that use this need that
 * split for the same reason from opposite directions: the public board is
 * statically rendered and revalidated on a timer, so a relative phrase baked
 * into the markup would be a lie by the time anybody read it, and the absolute
 * form is the one that stays true in cached HTML and for a reader without
 * scripts. On the dashboard the markup is fresh but the stamp is a scoring pass
 * that could be weeks old, and "ago" is the only form that answers the question
 * being asked of it.
 *
 * Shared rather than written twice: the board says when it was read and the
 * dashboard says when the ranking was last recomputed, and those should not
 * phrase the same interval differently.
 */
export function RelativeTime({ stamp }: { stamp: string }) {
  const [label, setLabel] = useState(() => absolute(new Date(stamp)));

  useEffect(() => {
    const at = new Date(stamp);
    const tick = () => {
      setLabel(relative(at, Date.now()));
    };
    tick();
    // A minute is the resolution of the smallest unit shown, so nothing on
    // screen can be stale by more than the step that would change it.
    const timer = setInterval(tick, MINUTE);
    return () => {
      clearInterval(timer);
    };
  }, [stamp]);

  return <time dateTime={stamp}>{label}</time>;
}
